import React, { useEffect, useState, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartTooltip, ResponsiveContainer, Legend,
  AreaChart, Area,
} from "recharts";

const useStyles = makeStyles((theme) => ({
  root: { flex: 1, padding: "20px 24px", overflowY: "auto", background: "#f8fafc" },
  filterBar: {
    display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
    marginBottom: 20, padding: "12px 16px",
    background: "#fff", borderRadius: 12,
    boxShadow: "0 1px 6px rgba(0,0,0,0.06)", border: "1px solid #f1f5f9",
  },
  periodLabel: { fontSize: 11, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginRight: 4 },
  periodBtn: (props) => ({
    padding: "5px 13px", borderRadius: 20, cursor: "pointer", fontSize: 12.5, transition: "all 0.15s",
    border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 500,
    "&:hover": { background: "#f1f5f9" },
  }),
  periodBtnActive: {
    border: "1.5px solid #3b82f6 !important",
    background: "#eff6ff !important",
    color: "#2563eb !important",
    fontWeight: "600 !important",
  },
  card: {
    borderRadius: 14, padding: "18px 20px",
    background: "#fff", boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
    border: "1px solid #f1f5f9", height: "100%",
    position: "relative", overflow: "hidden",
  },
  cardIcon: { fontSize: 28, marginBottom: 6 },
  cardLabel: { fontSize: 11.5, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 },
  cardValue: { fontSize: 30, fontWeight: 800, lineHeight: 1.1, marginTop: 3 },
  cardSub: { fontSize: 11, color: "#9ca3af", marginTop: 4 },
  cardAccent: {
    position: "absolute", top: 0, right: 0, width: 4, height: "100%", borderRadius: "0 14px 14px 0",
  },
  section: {
    background: "#fff", borderRadius: 14,
    boxShadow: "0 1px 6px rgba(0,0,0,0.06)", border: "1px solid #f1f5f9",
    padding: "20px 22px", marginTop: 16,
  },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 },
  tableHead: { display: "flex", padding: "0 0 8px", borderBottom: "2px solid #f1f5f9", marginBottom: 4 },
  tableCell: { fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.4 },
  tableRow: { display: "flex", alignItems: "center", padding: "9px 0", borderBottom: "1px solid #f8fafc" },
  badge: { display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, gap: 4 },
  funnelBar: { height: 32, borderRadius: 8, transition: "width 0.5s ease", display: "flex", alignItems: "center", paddingLeft: 12 },
  funnelLabel: { fontSize: 12, fontWeight: 600, color: "#fff", textShadow: "0 1px 2px rgba(0,0,0,0.3)" },
  productRow: { display: "flex", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f8fafc", gap: 10 },
  rankBadge: { width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 },
  progressBar: { flex: 1, height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" },
  progressFill: { height: "100%", borderRadius: 3, transition: "width 0.5s ease" },
  trendUp: { color: "#10b981", fontSize: 11, fontWeight: 600 },
  trendDown: { color: "#ef4444", fontSize: 11, fontWeight: 600 },
  emptyState: { color: "#9ca3af", fontSize: 13, textAlign: "center", padding: "30px 0" },
}));

const PERIODS = [
  { key: "today", label: "Hoje" },
  { key: "yesterday", label: "Ontem" },
  { key: "7d", label: "7 dias" },
  { key: "14d", label: "14 dias" },
  { key: "30d", label: "30 dias" },
  { key: "", label: "Todos" },
  { key: "custom", label: "Personalizado" },
];

const fmtMoney = (v) => {
  if (!v || v <= 0) return "R$ —";
  return "R$ " + parseFloat(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (d) => new Date(d).toLocaleDateString("pt-BR");

const FlowMetrics = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === "custom") { params.startDate = startDate; params.endDate = endDate; }
      const res = await api.get("/flow-metrics", { params });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [period, startDate, endDate]);

  useEffect(() => {
    if (period !== "custom" || (startDate && endDate)) load();
  }, [period, startDate, endDate, load]);

  const allStats = data?.allStats || [];

  // KPI calculations
  const pixPaid = allStats.filter(s => s.status === "pix_paid").reduce((a, b) => a + parseInt(b.total), 0);
  const pixPending = allStats.filter(s => s.status === "pix_pending").reduce((a, b) => a + parseInt(b.total), 0);
  const reminders = allStats.filter(s => s.status === "pix_reminder_sent").reduce((a, b) => a + parseInt(b.total), 0);
  const cancelled = allStats.filter(s => s.status === "pix_cancelled").reduce((a, b) => a + parseInt(b.total), 0);
  const pixGerado = allStats.filter(s => s.type === "Pix Gerado").reduce((a, b) => a + parseInt(b.total), 0);
  const pixTotal = pixGerado; // Pix gerados (para funil)
  const abandoned = allStats.filter(s => s.type === "abandoned").reduce((a, b) => a + parseInt(b.total), 0);
  const totalRevenue = allStats.filter(s => s.status === "pix_paid").reduce((a, b) => a + parseFloat(b.revenue || 0), 0);
  const totalFlowsSent = allStats.filter(s => s.status === "sent").reduce((a, b) => a + parseInt(b.total), 0);

  // Payment type breakdown
  const pixPaidPix = allStats.filter(s => s.status === "pix_paid" && (s.paymentType === "Pix" || s.paymentType === "PIX")).reduce((a, b) => a + parseInt(b.total), 0);
  const pixPaidCard = allStats.filter(s => s.status === "pix_paid" && (s.paymentType === "Cartão" || s.paymentType?.toUpperCase().includes("CREDIT"))).reduce((a, b) => a + parseInt(b.total), 0);
  const pixPaidBoleto = allStats.filter(s => s.status === "pix_paid" && (s.paymentType === "Boleto" || s.paymentType?.toUpperCase().includes("BILLET"))).reduce((a, b) => a + parseInt(b.total), 0);
  const revenueCard = allStats.filter(s => s.status === "pix_paid" && (s.paymentType === "Cartão" || s.paymentType?.toUpperCase().includes("CREDIT"))).reduce((a, b) => a + parseFloat(b.revenue || 0), 0);

  const convRate = pixTotal > 0 ? ((pixPaid / pixTotal) * 100).toFixed(1) : pixPaid > 0 ? "100" : "0";

  // Average ticket
  const avgTicket = pixPaid > 0 && totalRevenue > 0 ? totalRevenue / pixPaid : 0;

  // Chart data
  const chartData = (data?.dailyChart || []).slice(0, 30).reverse().map(d => ({
    date: new Date(d.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
    Pagamentos: parseInt(d.pagamentos) || 0,
    Disparos: parseInt(d.total) || 0,
    Receita: parseFloat(d.revenue) || 0,
  }));

  // Top products from recent paid
  const productMap = {};
  (data?.recentPaid || []).forEach(p => {
    const name = p.productName || "Sem produto";
    if (!productMap[name]) productMap[name] = { count: 0, revenue: 0 };
    productMap[name].count++;
    productMap[name].revenue += parseFloat(p.purchasePrice || 0);
  });
  const topProducts = Object.entries(productMap)
    .map(([name, v]) => ({ name, ...v }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);
  const maxProductRevenue = topProducts[0]?.revenue || 1;

  // Funnel data
  const totalRef = Math.max(pixTotal, pixPaid);
  const funnelSteps = [
    pixTotal > 0 ? { label: "Pix Gerado", count: pixTotal, color: "#3b82f6", pct: 100 } : null,
    reminders > 0 ? { label: "Lembrete Enviado", count: reminders, color: "#8b5cf6", pct: pixTotal > 0 ? Math.round((reminders / pixTotal) * 100) : 0 } : null,
    pixPaidPix > 0 ? { label: "Pix Confirmado ✅", count: pixPaidPix, color: "#10b981", pct: totalRef > 0 ? Math.round((pixPaidPix / totalRef) * 100) : 0 } : null,
    pixPaidCard > 0 ? { label: "Cartão de Crédito 💳", count: pixPaidCard, color: "#6366f1", pct: 100 } : null,
    pixPaidBoleto > 0 ? { label: "Boleto Pago 📄", count: pixPaidBoleto, color: "#f59e0b", pct: 100 } : null,
  ].filter(Boolean);

  const rankColors = ["#f59e0b", "#94a3b8", "#cd7f32", "#6b7280", "#6b7280"];

  const paymentSub = [pixPaidPix > 0 ? `${pixPaidPix} Pix` : null, pixPaidCard > 0 ? `${pixPaidCard} Cartão` : null, pixPaidBoleto > 0 ? `${pixPaidBoleto} Boleto` : null].filter(Boolean).join(" · ") || `${pixPaid} pagamentos`;

  const cards = [
    { label: "Receita Total", value: fmtMoney(totalRevenue), color: "#10b981", icon: "💰", sub: paymentSub, accent: "#10b981" },
    { label: "Ticket Médio", value: fmtMoney(avgTicket), color: "#059669", icon: "🎯", sub: `${pixPaid} vendas confirmadas`, accent: "#059669" },
    { label: "Vendas Confirmadas", value: pixPaid, color: "#10b981", icon: "✅", sub: `Taxa ${convRate}% de conversão`, accent: "#10b981" },
    { label: "Pix Pendentes", value: pixPending, color: "#f59e0b", icon: "⏳", sub: "Aguardando pagamento", accent: "#f59e0b" },
    { label: "Carrinhos Abandonados", value: abandoned, color: "#ef4444", icon: "🛒", sub: "Sem finalizar compra", accent: "#ef4444" },
    { label: "Fluxos Disparados", value: totalFlowsSent, color: "#3b82f6", icon: "⚡", sub: "Mensagens enviadas", accent: "#3b82f6" },
  ];

  return (
    <MainContainer>
      <MainHeader><Title>Métricas de Vendas e Fluxos</Title></MainHeader>
      <div className={classes.root}>

        {/* Filter bar */}
        <div className={classes.filterBar}>
          <span className={classes.periodLabel}>Período:</span>
          {PERIODS.map(p => (
            <button key={p.key}
              className={`${classes.periodBtn} ${period === p.key ? classes.periodBtnActive : ""}`}
              onClick={() => setPeriod(p.key)}
              style={{
                padding: "5px 13px", borderRadius: 20, cursor: "pointer", fontSize: 12.5,
                border: period === p.key ? "1.5px solid #3b82f6" : "1.5px solid #e5e7eb",
                background: period === p.key ? "#eff6ff" : "#fff",
                color: period === p.key ? "#2563eb" : "#374151",
                fontWeight: period === p.key ? 600 : 500,
              }}
            >{p.label}</button>
          ))}
          {period === "custom" && (
            <>
              <TextField type="date" size="small" variant="outlined" value={startDate}
                onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} label="De" style={{ width: 145 }} />
              <TextField type="date" size="small" variant="outlined" value={endDate}
                onChange={e => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} label="Até" style={{ width: 145 }} />
            </>
          )}
          {loading && <CircularProgress size={16} style={{ marginLeft: 8, color: "#3b82f6" }} />}
        </div>

        {/* KPI Cards */}
        <Grid container spacing={2}>
          {cards.map((card, i) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
              <Paper className={classes.card} elevation={0}>
                <div className={classes.cardAccent} style={{ background: card.accent }} />
                <div className={classes.cardIcon}>{card.icon}</div>
                <Typography className={classes.cardLabel}>{card.label}</Typography>
                <Typography className={classes.cardValue} style={{ color: card.color }}>
                  {loading ? <CircularProgress size={20} /> : card.value}
                </Typography>
                <Typography className={classes.cardSub}>{card.sub}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Charts row */}
        <Grid container spacing={2} style={{ marginTop: 0 }}>
          {/* Revenue + Payments area chart */}
          <Grid item xs={12} md={8}>
            <Paper className={classes.section} elevation={0} style={{ marginTop: 16 }}>
              <Typography className={classes.sectionTitle}>
                📈 Evolução de Receita e Pagamentos
              </Typography>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={210}>
                  <AreaChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                    <defs>
                      <linearGradient id="colorReceita" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorDisparos" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#10b981" }} />
                    <RechartTooltip
                      contentStyle={{ borderRadius: 10, border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", fontSize: 12 }}
                      formatter={(value, name) => name === "Receita" ? ["R$ " + parseFloat(value).toFixed(2), name] : [value, name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area yAxisId="left" type="monotone" dataKey="Disparos" stroke="#3b82f6" strokeWidth={2} fill="url(#colorDisparos)" dot={false} />
                    <Area yAxisId="left" type="monotone" dataKey="Pagamentos" stroke="#10b981" strokeWidth={2.5} fill="url(#colorReceita)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className={classes.emptyState}>Nenhum dado no período selecionado</div>
              )}
            </Paper>
          </Grid>

          {/* Conversion Funnel */}
          <Grid item xs={12} md={4}>
            <Paper className={classes.section} elevation={0} style={{ marginTop: 16 }}>
              <Typography className={classes.sectionTitle}>🔻 Funil de Conversão</Typography>
              {funnelSteps.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 8 }}>
                  {funnelSteps.map((step, i) => (
                    <div key={i}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#4b5563" }}>{step.label}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: step.color }}>{step.count}</span>
                      </div>
                      <div style={{ background: "#f1f5f9", borderRadius: 8, height: 28, overflow: "hidden" }}>
                        <div style={{
                          width: step.pct + "%", minWidth: step.pct > 0 ? 40 : 0,
                          height: "100%", background: step.color, borderRadius: 8,
                          display: "flex", alignItems: "center", paddingLeft: 10, transition: "width 0.6s ease",
                        }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#fff" }}>{step.pct}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(pixTotal > 0 || pixPaid > 0) && (
                    <div style={{ marginTop: 8, padding: "10px 14px", background: "#f0fdf4", borderRadius: 10, textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "#6b7280" }}>Taxa de conversão total</div>
                      <div style={{ fontSize: 28, fontWeight: 800, color: "#10b981" }}>{convRate}%</div>
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>{pixPaid} pago{pixPaid !== 1 ? "s" : ""}{pixTotal > 0 ? ` de ${pixTotal} Pix` : ""}</div>
                    </div>
                  )}
                </div>
              ) : (
                <div className={classes.emptyState}>
                  {loading ? <CircularProgress size={24} /> : "Sem dados de Pix no período"}
                </div>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Bottom row */}
        <Grid container spacing={2} style={{ marginTop: 0 }}>
          {/* Top Products */}
          <Grid item xs={12} md={5}>
            <Paper className={classes.section} elevation={0} style={{ marginTop: 0 }}>
              <Typography className={classes.sectionTitle}>🏆 Top Produtos Vendidos</Typography>
              {topProducts.length > 0 ? topProducts.map((p, i) => (
                <div key={i} className={classes.productRow}>
                  <div className={classes.rankBadge} style={{ background: rankColors[i] || "#e5e7eb", color: i < 3 ? "#fff" : "#374151" }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {p.name}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                      <div className={classes.progressBar}>
                        <div className={classes.progressFill} style={{ width: ((p.revenue / maxProductRevenue) * 100) + "%", background: rankColors[i] || "#6b7280" }} />
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#10b981" }}>{fmtMoney(p.revenue)}</div>
                    <div style={{ fontSize: 10.5, color: "#9ca3af" }}>{p.count} venda{p.count !== 1 ? "s" : ""}</div>
                  </div>
                </div>
              )) : (
                <div className={classes.emptyState}>{loading ? <CircularProgress size={24} /> : "Nenhuma venda no período"}</div>
              )}
            </Paper>
          </Grid>

          {/* Recent buyers */}
          <Grid item xs={12} md={7}>
            <Paper className={classes.section} elevation={0} style={{ marginTop: 0 }}>
              <Typography className={classes.sectionTitle}>💳 Compradores Recentes</Typography>
              <div className={classes.tableHead}>
                <span className={classes.tableCell} style={{ flex: 2 }}>Cliente</span>
                <span className={classes.tableCell} style={{ flex: 2 }}>Produto</span>
                <span className={classes.tableCell} style={{ flex: 1, textAlign: "right" }}>Valor / Data</span>
              </div>
              {(data?.recentPaid || []).slice(0, 10).map((p, i) => (
                <div key={i} className={classes.tableRow}>
                  <div style={{ flex: 2 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>
                      {p.customerName || "—"}
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>
                      {p.customerPhone ? "+" + p.customerPhone.replace(/\D/g, "").replace(/^55/, "55 ") : ""}
                    </div>
                  </div>
                  <div style={{ flex: 2, fontSize: 12, color: "#4b5563" }}>
                    {p.productName ? (p.productName.length > 24 ? p.productName.slice(0, 24) + "…" : p.productName) : "—"}
                  </div>
                  <div style={{ flex: 1, textAlign: "right" }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#10b981" }}>
                      {p.purchasePrice > 0 ? fmtMoney(p.purchasePrice) : "—"}
                    </div>
                    <div style={{ fontSize: 10.5, color: "#9ca3af" }}>{fmtDate(p.updatedAt)}</div>
                  </div>
                </div>
              ))}
              {(!data?.recentPaid || data.recentPaid.length === 0) && !loading && (
                <div className={classes.emptyState}>Nenhum pagamento no período selecionado</div>
              )}
              {loading && <div style={{ textAlign: "center", padding: 20 }}><CircularProgress size={24} /></div>}
            </Paper>
          </Grid>
        </Grid>

      </div>
    </MainContainer>
  );
};

export default FlowMetrics;
